import { InstitutionSingleResponse, IInstitution, IInstitutionContext, isFunction, isPromiseLike } from '@ngt/opms';
import { useMemo } from 'react';
import { KeyedMutator, MutatorCallback } from 'swr';
import { GetConsentEventDataResponse, Institution } from '../../api/dtos';
import useConsentEventData from '../data/useConsentEventData';



const useGenerateEConsentInstitutionContext = (consentEventContext: ReturnType<typeof useConsentEventData>) => {
    
    const institutionContext: IInstitutionContext = useMemo(() => {
        const institution = consentEventContext.data?.institution as IInstitution;
        const error = consentEventContext.error;

        const institutionMutate: KeyedMutator<InstitutionSingleResponse> = async (data, opts) => {
            let newData: GetConsentEventDataResponse = { ...(consentEventContext.data as any), responseStatus: error as any };
            let newSingleData: InstitutionSingleResponse | undefined = undefined;
            
            if (!data) {
                newSingleData = undefined;
            }
            else if (isFunction(data)) {
                const existingSingleData = { institution, responseStatus: error } as InstitutionSingleResponse;

                const callback = (data as MutatorCallback<InstitutionSingleResponse>)(existingSingleData);
                
                if (isPromiseLike(callback)) {
                    newSingleData = await (callback as Promise<InstitutionSingleResponse>);
                }
                else {
                    newSingleData = callback;
                }
            }
            else if (isPromiseLike(data)) {
                newSingleData = await (data as Promise<InstitutionSingleResponse>);
            }

            if (!newSingleData) {
                newData.institution = undefined as any;
            }
            else {
                if (newSingleData.responseStatus) {
                    newData.responseStatus = newSingleData.responseStatus;
                }

                newData.institution = newSingleData.institution as Institution;
            }

            const updatedData = await consentEventContext.mutate(newData, opts as any);

            if (updatedData) {
                return {
                    institution: updatedData.institution,
                    responseStatus: updatedData.responseStatus
                } as InstitutionSingleResponse
            }

            return undefined;
        }

        const institutionRefresh = async () => {
            const newData = await consentEventContext.refresh();

            return newData?.institution;
        }

        return {
            data: institution,
            error: consentEventContext.error,
            loading: consentEventContext.loading,
            validating: consentEventContext.validating,
            mutate: institutionMutate,
            refresh: institutionRefresh
        }
    }, [consentEventContext])

    return institutionContext
}

export default useGenerateEConsentInstitutionContext;